import type { ErrorADT } from "pb.safe";
import type { Identity } from "pb.types";
import { v4 as uuidv4 } from "uuid";

import {
	WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE,
	type Payload,
	type POSConnectorClient,
	type POSConnectorMessage,
} from "../../../@lib-pos-connector-client";
import { logger } from "../../../shared/logger";

/**
 * An inbound frame past the message guard: protocol errors are handled there,
 * so `error` is never set by the time a flow's frame handler sees it.
 */
export type POSConnectorFrame = Identity<Omit<POSConnectorMessage, "error">>;

export const STATE_INITIAL_TIMEOUT = 15000; // 15 seconds
export const STATE_TRANSACTION_STATE_TIMEOUT = 40000; // 40 seconds

export function CancelRequest(sessionId: string) {
	return {
		action: "transactionEvent",
		eventId: uuidv4(),
		timestamp: Date.now().toString(),
		data: {
			sessionUuid: sessionId,
			event: "CANCEL",
		},
	} satisfies Payload;
}

/**
 * Acknowledges a terminal response so the terminal knows the result was
 * consumed. Sent from both the in-flight and cancelling response handlers.
 */
export function AcknowledgedResponse(deps: {
	action: "purchase" | "refund";
	sessionId: string;
	requestId: string;
}) {
	return {
		action: deps.action,
		eventId: uuidv4(),
		requestId: deps.requestId,
		timestamp: Date.now().toString(),
		data: {
			sessionUuid: deps.sessionId,
			state: "ACKNOWLEDGED",
		},
	} satisfies Payload;
}

export type SilenceCause = "inactivity" | "network" | "disconnected";

/**
 * Silence on a transaction socket is ambiguous: an idle merchant, a socket
 * that has already gone away, or this host losing its link all look the same
 * from here. Cancels on a live socket, then settles which it was: a failed
 * healthcheck means `network`, a dead socket on a reachable host means
 * `disconnected`, and only a live socket on a reachable host is `inactivity`.
 */
export async function classifySilence(deps: {
	connection: Pick<POSConnectorClient, "ready" | "send">;
	deviceId: string;
	healthcheck: (
		deviceId: string,
	) => Promise<undefined | ErrorADT<"NetworkError">>;
	sessionId: string;
}): Promise<SilenceCause> {
	const ready = deps.connection.ready;
	if (ready) {
		deps.connection.send(CancelRequest(deps.sessionId));
	}
	let cause: SilenceCause = ready ? "inactivity" : "disconnected";
	if ((await deps.healthcheck(deps.deviceId)) instanceof Error) {
		cause = "network";
	}
	logger.warn("pos connector: transaction silence", { cause, ready });
	return cause;
}

/**
 * Settles a silent socket. `network` and `disconnected` mean the same thing
 * wherever the silence began, so each flow builds those two pages once here;
 * only what a live socket implies differs, and the caller supplies that per
 * timer. Before the terminal acknowledges, a live socket means it never picked
 * the request up; mid-transaction it means the merchant is idle.
 *
 * `retry` is per call: a silent request and a silent transaction do not always
 * resume from the same place.
 *
 * `timer` is read before and after the healthcheck: a retry or cancel re-arms
 * the flow's timer while it is in flight, and that stale classification must
 * not terminate the new attempt.
 */
export function createCloseOnSilence<
	TPage extends { onClose: () => void },
>(deps: {
	connection: Pick<POSConnectorClient, "ready" | "send">;
	deviceId: string;
	healthcheck: (
		deviceId: string,
	) => Promise<undefined | ErrorADT<"NetworkError">>;
	sessionId: string;
	done: (page: TPage, onNonInteractive: "onClose") => void;
	isLocked: () => boolean;
	timer: () => unknown;
	network: (retry: () => void) => TPage;
	disconnected: (retry: () => void) => TPage;
}) {
	return async (livePage: () => TPage, retry: () => void) => {
		const timer = deps.timer();
		const cause = await classifySilence(deps);
		// Another path may complete or re-arm the flow while the healthcheck is
		// in flight.
		if (deps.isLocked() || deps.timer() !== timer) {
			return;
		}
		return void deps.done(
			{
				network: () => deps.network(retry),
				disconnected: () => deps.disconnected(retry),
				inactivity: livePage,
			}[cause](),
			"onClose",
		);
	};
}

/**
 * The copy of the page a terminator is attached to, so the error the host
 * receives carries the words the merchant just read. Matched structurally
 * rather than imported, to keep this off the render modules.
 *
 * `message` is required: a title alone tends to restate the error type, which
 * tells the host nothing it did not already have.
 */
export type CloseCopy = { message: string };

/**
 * Builds the shared `closeWith*` terminators used by the refund/purchase
 * orchestrators. `resolveError` is the flow's `resolve.Error`, so the
 * flow-typed transport resolver stays per-flow.
 *
 * Each terminator is a factory rather than a handler taking the copy
 * positionally, because UI close handlers forward the click event.
 */
export function createCloseHandlers(deps: {
	resolveError: (
		type:
			| "Cancelled"
			| "Connection Failure"
			| "Inactivity Timeout"
			| "Network Failure"
			| "Paired Device Busy",
		options: { message: string },
	) => void;
}) {
	return {
		closeWithCancelled(copy: CloseCopy) {
			return () => {
				logger.warn("pos connector: closing with cancelled");
				return void deps.resolveError("Cancelled", { message: copy.message });
			};
		},
		closeWithConnectionFailure(copy: CloseCopy) {
			return () => {
				logger.warn("pos connector: closing with connection failure");
				return void deps.resolveError("Connection Failure", {
					message: copy.message,
				});
			};
		},
		closeWithInactivityTimeout(copy: CloseCopy) {
			return () => {
				logger.warn("pos connector: closing with inactivity timeout");
				return void deps.resolveError("Inactivity Timeout", {
					message: copy.message,
				});
			};
		},
		closeWithNetworkFailure(copy: CloseCopy) {
			return () => {
				logger.warn("pos connector: closing with network failure");
				return void deps.resolveError("Network Failure", {
					message: copy.message,
				});
			};
		},
		closeWithPairedDeviceBusy(copy: CloseCopy) {
			return () => {
				logger.warn("pos connector: closing with paired device busy");
				return void deps.resolveError("Paired Device Busy", {
					message: copy.message,
				});
			};
		},
	};
}

type ErrorPageCopy = Record<
	| "ErrorDisconnected"
	| "ErrorInactivityTimeout"
	| "ErrorNetwork"
	| "ErrorOperation"
	| "ErrorTerminalMissing"
	| "ErrorTerminalUnreachable",
	CloseCopy
>;

type Retryable<TPage> = (props: {
	onClose: () => void;
	onRetry: () => void;
}) => TPage;

/**
 * The error pages every flow terminates on, wired once per attempt: each pairs
 * its page with the terminator carrying the same copy, so the reconnector,
 * message guard and silence classifier take a factory rather than each flow
 * restating the `onClose` / `onRetry` pairing.
 *
 * `retry` defaults to the flow's own retry; the matched refund passes a
 * different one where the terminal is unreachable.
 *
 * Pass the flow's closable page union as `TPage`: each constructor returns its
 * own variant, so inference has no single type to settle on.
 */
export function createErrorPages<TPage>(deps: {
	Page: {
		ErrorDisconnected: Retryable<TPage>;
		ErrorInactivityTimeout: Retryable<TPage>;
		ErrorNetwork: Retryable<TPage>;
		ErrorOperation: Retryable<TPage>;
		ErrorTerminalMissing: (props: { onClose: () => void }) => TPage;
		ErrorTerminalUnreachable: Retryable<TPage>;
	};
	copy: ErrorPageCopy;
	close: Pick<
		ReturnType<typeof createCloseHandlers>,
		| "closeWithConnectionFailure"
		| "closeWithInactivityTimeout"
		| "closeWithNetworkFailure"
	>;
	onRetry: () => void;
}) {
	const { Page, copy, close, onRetry } = deps;
	return {
		disconnected: (retry = onRetry) =>
			Page.ErrorDisconnected({
				onClose: close.closeWithConnectionFailure(copy.ErrorDisconnected),
				onRetry: retry,
			}),
		inactivity: () =>
			Page.ErrorInactivityTimeout({
				onClose: close.closeWithInactivityTimeout(copy.ErrorInactivityTimeout),
				onRetry,
			}),
		network: (retry = onRetry) =>
			Page.ErrorNetwork({
				onClose: close.closeWithNetworkFailure(copy.ErrorNetwork),
				onRetry: retry,
			}),
		operation: () =>
			Page.ErrorOperation({
				onClose: close.closeWithConnectionFailure(copy.ErrorOperation),
				onRetry,
			}),
		terminalMissing: () =>
			Page.ErrorTerminalMissing({
				onClose: close.closeWithConnectionFailure(copy.ErrorTerminalMissing),
			}),
		terminalUnreachable: (retry = onRetry) =>
			Page.ErrorTerminalUnreachable({
				onClose: close.closeWithConnectionFailure(
					copy.ErrorTerminalUnreachable,
				),
				onRetry: retry,
			}),
	};
}

/**
 * Owns the reconnection policy shared by the refund/purchase orchestrators:
 * capped attempts with linear backoff, healthcheck-gated retries, and the
 * normal-closure guard. Page construction stays in-flow via `onExhausted` /
 * `onNetworkError`; `connect` re-enters the flow's own connect routine.
 * Callers `reset()` when data is successfully received.
 */
export function createReconnector(deps: {
	deviceId: string;
	healthcheck: (
		deviceId: string,
	) => Promise<undefined | ErrorADT<"NetworkError">>;
	updateTimeout: (callback: () => void, delay: number) => void;
	connect: () => void;
	onExhausted: () => void;
	onNetworkError: () => void;
}) {
	let _attempt = 0;

	function reconnect() {
		logger.warn("pos connector: reconnecting", { attempt: _attempt });

		if (_attempt >= 5) {
			logger.warn("pos connector: closing with error disconnected");
			return void deps.onExhausted();
		}

		deps.updateTimeout(async () => {
			// checks api resolution; retries with refreshed token on 401.
			// success permits web socket reconnect, failure signals network
			// error.
			const result = await deps.healthcheck(deps.deviceId);

			if (result instanceof Error) {
				return void deps.onNetworkError();
			}

			_attempt += 1;
			deps.connect();
		}, 1000 * _attempt);
	}

	function onClose(event: CloseEvent) {
		logger.debug("pos connector: on close", {
			code: event.code,
			event: event,
		});

		if (event.code === WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE) {
			return;
		}

		logger.error("pos connector: abnormal close", {
			eventCode: event.code,
			eventReason: event.reason,
			eventWasClean: event.wasClean,
		});

		return void reconnect();
	}

	function onError(event: Event) {
		logger.error("pos connector: on error", { event: event });
		return void reconnect();
	}

	return {
		reconnect,
		onClose,
		onError,
		reset() {
			_attempt = 0;
		},
		get attempt() {
			return _attempt;
		},
	};
}

/**
 * Builds the shared requestId guard. A frame with no requestId is warned about
 * and accepted, since the terminal may predate the field; a frame carrying a
 * different requestId belongs to a superseded attempt and is ignored.
 *
 * `context` is logged as-is, so pass only what is needed to trace the frame —
 * never the frame itself, which carries cardholder data on responses.
 */
export function createRequestIdGuard(current: () => string) {
	return function matchesRequestId(
		requestId: string | undefined,
		context: Record<string, unknown>,
	): boolean {
		if (!requestId) {
			logger.warn("pos connector: frame missing requestId", context);
			return true;
		}

		if (requestId !== current()) {
			logger.warn("pos connector: frame requestId mismatch, ignoring", {
				...context,
				expected: current(),
				received: requestId,
			});
			return false;
		}

		return true;
	};
}

/**
 * Builds the shared `handle()` message-error guard: transport-level errors are
 * logged and swallowed, protocol errors are mapped to the flow's callbacks
 * (`PAIRED_DEVICE_UNREACHABLE` → `onUnreachable`, `FORBIDDEN` → `onForbidden`,
 * anything else → `onOperationError`). Which page each error becomes stays
 * in-flow.
 */
export function createMessageGuard(deps: {
	onUnreachable: () => void;
	onForbidden: () => void;
	onOperationError: () => void;
}) {
	return function handle(callback: (message: POSConnectorFrame) => void) {
		return (message: POSConnectorMessage | Error): void => {
			if (message instanceof Error) {
				return void logger.error("pos connector: on message error", {
					errorName: message.name,
					errorMessage: message.message,
				});
			}
			if (message.error) {
				if (message.error.type === "PAIRED_DEVICE_UNREACHABLE") {
					return void deps.onUnreachable();
				}
				if (message.error.type === "FORBIDDEN") {
					return void deps.onForbidden();
				}
				return void deps.onOperationError();
			}

			return void callback(message);
		};
	};
}
